import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import Header from "./Header";
import Footer from "./Footer";
import {
  ArrowLeft,
  ArrowRight,
  Home as HomeIcon,
  DollarSign,
  Bus,
  Bed,
  ShieldCheck,
  Wrench,
  Clock,
  CheckCircle2,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { colors } from "../config/site";

/**
 * Apartment Shopping Challenge — decision-making game.
 * Player is helping "Simone" (a persona) pick between 3 apartments.
 * Each has visible trade-offs. The "right" answer isn't the cheapest.
 */

const PERSONA = {
  name: "Simone",
  summary:
    "Simone is a 32-year-old single mom with a 7-year-old daughter. She works full-time in North Philly. Her take-home pay is $2,800/month. She'd like to keep her commute under 45 minutes and needs a safe neighborhood near a good school.",
  monthlyIncome: 2800,
  maxCommute: 45,
  priorities: [
    "Safety and a good neighborhood",
    "Reasonable commute (under 45 min)",
    "Rent under 30% of take-home ($840 max, or she stretches to $940)",
    "Stable landlord (long-term rental)",
  ],
};

const APARTMENTS = [
  {
    id: "A",
    name: "Apartment A",
    tagline: "Big & cheap",
    rent: 750,
    utilitiesEst: 220, // not included
    utilitiesIncluded: false,
    bedrooms: 2,
    commuteMinutes: 20,
    neighborhoodScore: "Rough — high 911 call volume",
    landlord: "Investor with 12 units, high turnover",
    leaseTerm: "12 months, auto-renew for 24 more",
    condition: "Old appliances, some water damage in ceiling",
    petPolicy: "No pets, no exceptions",
  },
  {
    id: "B",
    name: "Apartment B",
    tagline: "Great neighborhood, tight budget",
    rent: 900,
    utilitiesEst: 100,
    utilitiesIncluded: false,
    bedrooms: 2,
    commuteMinutes: 40,
    neighborhoodScore: "Safe, near a well-rated school",
    landlord: "Local family owner, 15+ year history",
    leaseTerm: "12 months, standard 60-day notice",
    condition: "Recently renovated kitchen and bath",
    petPolicy: "Cats allowed with deposit",
  },
  {
    id: "C",
    name: "Apartment C",
    tagline: "Luxury feel",
    rent: 1150,
    utilitiesEst: 0,
    utilitiesIncluded: true,
    bedrooms: 2,
    commuteMinutes: 25,
    neighborhoodScore: "Trendy area, gentrifying quickly",
    landlord: "Corporate property manager, no on-site rep",
    leaseTerm: "12 months, 15% rent increase expected at renewal",
    condition: "Brand new finishes, in-unit laundry",
    petPolicy: "Pets allowed with $500 fee",
  },
];

const CORRECT_ID = "B";

const analysis = (id) => {
  if (id === "A")
    return {
      title: "Cheapest — but expensive in the long run",
      color: colors.terracotta,
      points: [
        `Rent + utilities = about $${750 + 220} — cheap on paper.`,
        "But the neighborhood safety concerns put her daughter at risk.",
        "High-turnover landlord = poor maintenance response.",
        "The auto-renew clause locks her in for 3 total years — very hard to leave.",
        "Water damage means the landlord may already be neglecting the property.",
      ],
      verdict:
        "Cheap rent isn't cheap when you consider risk, stress, and being locked into a hostile lease. Not a smart pick.",
    };
  if (id === "B")
    return {
      title: "The smart choice",
      color: colors.primary,
      points: [
        `Rent + utilities ≈ $1,000. Just at Simone's 30% stretch limit.`,
        "Safe neighborhood + good school = huge quality-of-life win for her daughter.",
        "Local family landlord with 15+ year track record = things get fixed.",
        "Standard 60-day notice = flexibility, not a trap.",
        "40-minute commute is under her 45-min tolerance.",
      ],
      verdict:
        "Not the flashiest and not the cheapest — but the smartest. This is the one that keeps her housed long-term.",
    };
  return {
    title: "Beautiful — but a budget breaker",
    color: colors.terracotta,
    points: [
      `Rent is $1,150 — that's 41% of Simone's take-home. Way over the 30% rule.`,
      "Utilities are included, but she'll be house-poor with no cushion.",
      "The 15% renewal increase means rent could jump to $1,320 next year.",
      "Corporate management with no on-site rep = long hold times when things break.",
      "Gentrifying areas can push her out within 2 years.",
    ],
    verdict:
      "The apartment might make her feel like she made it — but the math doesn't work. Best case, she's paycheck-to-paycheck. Worst case, she's evicted at renewal.",
  };
};

const StatRow = ({ icon: Icon, label, value, small }) => (
  <div className="flex items-start gap-2 py-1.5 text-sm">
    <Icon
      className="w-4 h-4 mt-0.5 flex-shrink-0"
      style={{ color: colors.textMuted }}
    />
    <div className="flex-grow">
      <p className="font-semibold" style={{ color: colors.charcoal }}>
        {label}
      </p>
      <p className={small ? "text-xs" : "text-sm"} style={{ color: colors.textMuted }}>
        {value}
      </p>
    </div>
  </div>
);

const ApartmentShopping = () => {
  const [step, setStep] = useState(0); // 0 = intro, 1 = choose, 2 = reveal
  const [choice, setChoice] = useState(null);

  const isIntro = step === 0;
  const isChoosing = step === 1;
  const isReveal = step === 2;

  const chosenAnalysis = choice ? analysis(choice) : null;
  const correctAnalysis = analysis(CORRECT_ID);
  const isCorrect = choice === CORRECT_ID;

  const restart = () => {
    setChoice(null);
    setStep(0);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />
      <main className="pt-28 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <Link
            to="/resources"
            className="inline-flex items-center text-sm font-semibold mb-6"
            style={{ color: colors.primary }}
            data-testid="apartments-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Link>

          {/* INTRO */}
          {isIntro && (
            <div
              className="rounded-3xl shadow-lg overflow-hidden bg-white p-8 lg:p-12"
              data-testid="apartments-intro"
            >
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
                style={{ backgroundColor: colors.gold + "44", color: colors.charcoal }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Decision-Making Simulation
              </div>
              <h1
                className="text-3xl lg:text-5xl font-bold leading-tight mb-5"
                style={{ color: colors.charcoal }}
              >
                Apartment Shopping Challenge
              </h1>
              <p className="text-lg mb-6" style={{ color: colors.textMuted }}>
                The cheapest apartment isn&apos;t always the smartest one. Meet Simone. Help her pick
                between three real options — and learn how to weigh trade-offs the way
                successful renters do.
              </p>
              <div
                className="rounded-2xl p-5 mb-7"
                style={{ backgroundColor: colors.cream }}
              >
                <p
                  className="text-xs uppercase tracking-widest font-bold mb-2"
                  style={{ color: colors.sage }}
                >
                  Meet Simone
                </p>
                <p className="text-sm mb-3" style={{ color: colors.charcoal }}>
                  {PERSONA.summary}
                </p>
                <p
                  className="text-xs uppercase tracking-widest font-bold mb-2"
                  style={{ color: colors.terracotta }}
                >
                  Her priorities
                </p>
                <ul className="space-y-1 text-sm" style={{ color: colors.charcoal }}>
                  {PERSONA.priorities.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span
                        className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: colors.terracotta }}
                      />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                onClick={() => setStep(1)}
                className="rounded-full px-7 py-6 text-base font-semibold text-white shadow-md hover:shadow-xl transition-all"
                style={{ backgroundColor: colors.primary }}
                data-testid="apartments-start-btn"
              >
                Compare the Apartments
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}

          {/* CHOOSING */}
          {(isChoosing || isReveal) && (
            <>
              <div className="mb-6">
                <h1
                  className="text-2xl lg:text-3xl font-bold mb-2"
                  style={{ color: colors.charcoal }}
                >
                  {isChoosing ? "Which apartment should Simone pick?" : "The Verdict"}
                </h1>
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  {isChoosing
                    ? "Read all three. Then tap the one you think is right for her."
                    : "Here's how each apartment stacks up against Simone's real needs."}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-5 mb-8">
                {APARTMENTS.map((apt) => {
                  const isChosen = choice === apt.id;
                  const isCorrectApt = apt.id === CORRECT_ID;
                  const revealHighlight = isReveal
                    ? isCorrectApt
                      ? { border: colors.primary, badge: "Best Match" }
                      : isChosen
                      ? { border: colors.terracotta, badge: "Your Pick" }
                      : { border: "#E6DFD2", badge: null }
                    : isChosen
                    ? { border: colors.primary, badge: "Your Pick" }
                    : { border: "#E6DFD2", badge: null };

                  return (
                    <button
                      key={apt.id}
                      type="button"
                      onClick={() => !isReveal && setChoice(apt.id)}
                      disabled={isReveal}
                      className="text-left rounded-3xl border-2 bg-white overflow-hidden transition-all hover:shadow-lg disabled:cursor-default"
                      style={{ borderColor: revealHighlight.border }}
                      data-testid={`apartment-${apt.id}`}
                    >
                      <div
                        className="px-5 py-4 flex items-center justify-between"
                        style={{
                          background:
                            isReveal && isCorrectApt
                              ? `linear-gradient(90deg, ${colors.primary}, ${colors.primaryDark})`
                              : isReveal && isChosen && !isCorrectApt
                              ? `linear-gradient(90deg, ${colors.terracotta}, ${colors.terracottaDark})`
                              : colors.cream,
                          color: isReveal && (isCorrectApt || isChosen) ? colors.white : colors.charcoal,
                        }}
                      >
                        <div>
                          <p className="text-xs uppercase tracking-widest font-bold opacity-80">
                            {apt.name}
                          </p>
                          <p className="text-lg font-bold">{apt.tagline}</p>
                        </div>
                        {revealHighlight.badge && (
                          <span
                            className="text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-widest"
                            style={{ backgroundColor: colors.white, color: colors.charcoal }}
                          >
                            {revealHighlight.badge}
                          </span>
                        )}
                      </div>
                      <div className="p-5 space-y-1">
                        <StatRow icon={DollarSign} label="Rent" value={`$${apt.rent}/mo`} />
                        <StatRow
                          icon={DollarSign}
                          label="Utilities"
                          value={
                            apt.utilitiesIncluded
                              ? "Included"
                              : `~$${apt.utilitiesEst}/mo extra`
                          }
                          small
                        />
                        <StatRow icon={Bed} label="Bedrooms" value={apt.bedrooms} />
                        <StatRow
                          icon={Bus}
                          label="Commute"
                          value={`~${apt.commuteMinutes} min to work`}
                        />
                        <StatRow
                          icon={ShieldCheck}
                          label="Neighborhood"
                          value={apt.neighborhoodScore}
                        />
                        <StatRow
                          icon={HomeIcon}
                          label="Landlord"
                          value={apt.landlord}
                          small
                        />
                        <StatRow icon={Clock} label="Lease Terms" value={apt.leaseTerm} small />
                        <StatRow
                          icon={Wrench}
                          label="Condition"
                          value={apt.condition}
                          small
                        />
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Submit button */}
              {isChoosing && (
                <div className="flex justify-end">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!choice}
                    className="rounded-full px-7 py-6 font-semibold text-white"
                    style={{ backgroundColor: colors.terracotta }}
                    data-testid="apartments-submit-btn"
                  >
                    See the Verdict
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Reveal */}
              {isReveal && (
                <div className="space-y-6" data-testid="apartments-reveal">
                  {/* Verdict on user's pick */}
                  {!isCorrect && (
                    <div
                      className="rounded-2xl p-6"
                      style={{
                        backgroundColor: colors.terracotta + "1a",
                        borderLeft: `4px solid ${colors.terracotta}`,
                      }}
                    >
                      <p
                        className="text-xs uppercase tracking-widest font-bold mb-2"
                        style={{ color: colors.terracottaDark }}
                      >
                        Your pick — {chosenAnalysis.title}
                      </p>
                      <ul className="space-y-1.5 text-sm mb-3" style={{ color: colors.charcoal }}>
                        {chosenAnalysis.points.map((p, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span
                              className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: colors.terracotta }}
                            />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm italic" style={{ color: colors.charcoal }}>
                        {chosenAnalysis.verdict}
                      </p>
                    </div>
                  )}

                  {/* Correct answer */}
                  <div
                    className="rounded-2xl p-6"
                    style={{
                      backgroundColor: colors.sage + "1a",
                      borderLeft: `4px solid ${colors.primary}`,
                    }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-6 h-6" style={{ color: colors.primary }} />
                      <p
                        className="text-lg font-bold"
                        style={{ color: colors.primary }}
                      >
                        {isCorrect ? "You nailed it — Apartment B." : "The best pick is Apartment B."}
                      </p>
                    </div>
                    <p
                      className="text-xs uppercase tracking-widest font-bold mb-2"
                      style={{ color: colors.sage }}
                    >
                      Why it&apos;s the smart choice
                    </p>
                    <ul className="space-y-1.5 text-sm mb-3" style={{ color: colors.charcoal }}>
                      {correctAnalysis.points.map((p, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2
                            className="w-4 h-4 mt-0.5 flex-shrink-0"
                            style={{ color: colors.sage }}
                          />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm italic" style={{ color: colors.charcoal }}>
                      {correctAnalysis.verdict}
                    </p>
                  </div>

                  {/* Big takeaway */}
                  <div
                    className="rounded-3xl p-6 lg:p-7 text-white"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                    }}
                  >
                    <h3 className="text-xl lg:text-2xl font-bold mb-2">
                      The Cheapest Apartment Costs the Most
                    </h3>
                    <p className="text-sm opacity-90 mb-5">
                      Smart renters weigh <strong>total monthly cost</strong> (rent + utilities),
                      <strong> neighborhood</strong>, <strong>landlord track record</strong>, and
                      <strong> lease flexibility</strong> — not just the sticker price. This is one
                      of the biggest lessons our workshops teach.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/programs">
                        <Button
                          className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto"
                          style={{ backgroundColor: colors.terracotta, color: colors.white }}
                        >
                          See Our Programs
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to="/resources">
                        <Button className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto border-white/40 border bg-transparent hover:bg-white/10">
                          Back to Resources
                        </Button>
                      </Link>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <Button
                      onClick={restart}
                      variant="ghost"
                      className="rounded-full"
                      data-testid="apartments-restart-btn"
                    >
                      <RefreshCw className="mr-2 w-4 h-4" />
                      Try Again
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ApartmentShopping;
